import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX } from 'react-icons/fi';
import { validateShipmentTrackingNumber } from '../../../../helpers/formHelpers/formHelpers';
import { Input } from '../../../base/forms/input';
import { P } from '../../../base/typography';
import { useMultiStepFormContext } from '../../context/multistepContext';
import { useTranslationsContext } from '../../context/translationsContext';
import InactiveStep from '../../InactiveStep';
import { FormStepProps } from '../../sendReceiptMultistep';
import {
	ButtonWrapper,
	ErrorMessage,
	FormStepHeading,
	FormStepWrapper,
	ProceedButton,
} from '../../sendReceiptMultistep.styles';
import * as StyledTrackingNumbers from './TrackingNumbers.styles';

const TRACKING_NUMBER_STEP_INDEX = 0;

export default function TrackingNumbers({
	setFieldValue,
	setFieldTouched,
	touched,
	errors,
	values,
	resetForm,
}: FormStepProps) {
	const { setLatestStepIndexVisited, currentStepIndex, next, goTo } = useMultiStepFormContext();
	const translations = useTranslationsContext();
	const { t } = useTranslation('sendReceiptMultistep');
	const [trackingNumberInput, setTrackingNumberInput] = useState('');
	const [trackingNumberValidationError, setTrackingNumberValidationError] = useState(false);

	const flashErrorMessage = () => {
		setTrackingNumberValidationError(true);
		setTimeout(() => {
			setTrackingNumberValidationError(false);
		}, 3000);
	};

	const addTrackingNumber = () => {
		setFieldTouched('trackingNumbers', true);
		if (!validateShipmentTrackingNumber(trackingNumberInput)) {
			flashErrorMessage();
		} else {
			const newTrackingNumbers = [...values.trackingNumbers, trackingNumberInput];
			setTrackingNumberInput('');
			setFieldValue('trackingNumbers', [...newTrackingNumbers]);
		}
	};

	const removeAcceptedTrackingNumber = indexToRemove => {
		const newTrackingNumbers = values.trackingNumbers.slice();
		newTrackingNumbers.splice(indexToRemove, 1);
		setFieldValue('trackingNumbers', [...newTrackingNumbers]);
		if (newTrackingNumbers.length === 0) {
			setLatestStepIndexVisited(0);
			resetForm({ errors: { trackingNumbers: 'trackingnumber-min' }, touched: { trackingNumbers: true } });
		}
	};

	if (currentStepIndex !== TRACKING_NUMBER_STEP_INDEX)
		return (
			<InactiveStep
				heading={translations?.inactiveTitles?.trackingNumbers || t('inactiveTitles.trackingNumbers')}
				onClick={() => goTo(TRACKING_NUMBER_STEP_INDEX)}
				touched={touched}
				errors={errors}
			/>
		);

	return (
		<FormStepWrapper>
			<FormStepHeading>{`${
				translations?.formStepTitles?.trackingNumbers || t('formStepTitles.trackingNumbers')
			}`}</FormStepHeading>

			<StyledTrackingNumbers.InputWrapper>
				<Input
					type="text"
					value={trackingNumberInput}
					onChange={e => setTrackingNumberInput(e.target.value)}
					onKeyDown={e => {
						if (e.key === 'Enter') {
							e.preventDefault();
							addTrackingNumber();
						}
					}}
					placeholder={`${translations?.trackingNumbers?.enter || t('trackingNumbers.enter')}`}
				/>
				<StyledTrackingNumbers.AddButton type="button" primary mobileBlock onClick={addTrackingNumber}>
					{translations?.trackingNumbers?.addButton || t('trackingNumbers.addButton')}
				</StyledTrackingNumbers.AddButton>
			</StyledTrackingNumbers.InputWrapper>
			{trackingNumberValidationError && (
				<ErrorMessage>
					{translations?.trackingNumbers?.errors?.validation || t(`trackingNumbers.errors.validation`)}
				</ErrorMessage>
			)}
			{touched.trackingNumbers && errors.trackingNumbers && (
				<ErrorMessage>
					{translations?.trackingNumbers?.errors?.[
						errors.trackingNumbers as keyof typeof translations.trackingNumbers.errors
					] || t(`trackingNumbers.errors.${errors.trackingNumbers}`)}
				</ErrorMessage>
			)}
			<StyledTrackingNumbers.AcceptedTrackingNumbers className="acceptedTrackingNumbers">
				{values.trackingNumbers.map((item, index) => (
					<StyledTrackingNumbers.AcceptedTrackingNumber key={index}>
						<P>{item}</P>
						<FiX onClick={() => removeAcceptedTrackingNumber(index)} />
					</StyledTrackingNumbers.AcceptedTrackingNumber>
				))}
			</StyledTrackingNumbers.AcceptedTrackingNumbers>
			<ButtonWrapper>
				<ProceedButton
					type="button"
					onClick={e => {
						e.stopPropagation();
						next();
					}}
					disabled={values.trackingNumbers.length === 0}>
					{translations?.nextStep || t('nextStep')}
				</ProceedButton>
			</ButtonWrapper>
		</FormStepWrapper>
	);
}
